import React, { useState, useEffect } from 'react';
import { 
  Home, 
  Users, 
  Building2, 
  Search, 
  Trophy, 
  History,
  Settings,
  BarChart3,
  Gift,
  Package,
  FileText,
  Minus,
  MessageSquare,
  Calendar
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLocation, Link } from 'react-router-dom';

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [hasRentalAccess, setHasRentalAccess] = useState(false);

  useEffect(() => {
    const checkRentalAccess = async () => {
      if (user?.companyId && user?.role === 'company') {
        try {
          const { companyService } = await import('../../services/companyService');
          const access = await companyService.hasRentalAccess(user.companyId);
          setHasRentalAccess(access);
        } catch (error) {
          console.error('Erro ao verificar acesso aos aluguéis:', error);
          setHasRentalAccess(false);
        }
      }
    };

    checkRentalAccess();
  }, [user?.companyId, user?.role]);

  const adminMenuItems = [
    { path: '/admin', icon: Home, label: 'Dashboard' },
    { path: '/admin/companies', icon: Building2, label: 'Empresas' },
    { path: '/admin/history', icon: History, label: 'Histórico' },
    { path: '/admin/points', icon: Trophy, label: 'Pontos' },
    { path: '/admin/sms-settings', icon: MessageSquare, label: 'Configurações SMS' },
  ];

  const companyMenuItems = [
    { path: '/company', icon: Home, label: 'Dashboard' },
    { path: '/company/customers', icon: Users, label: 'Clientes' },
    { path: '/company/points', icon: Trophy, label: 'Pontos' },
    { path: '/company/search', icon: Search, label: 'Consultar Pontos' },
    { path: '/company/rewards', icon: Gift, label: 'Prêmios' },
    { path: '/company/rewards-history', icon: History, label: 'Movimentação de Prêmios' },
    { path: '/company/redemptions', icon: Package, label: 'Resgates' },
    { path: '/company/rentals', icon: Calendar, label: 'Aluguéis' },
    { path: '/company/force-redemption', icon: Minus, label: 'Forçar Resgate' },
    { path: '/company/history', icon: History, label: 'Histórico Geral' },
    { path: '/company/customer-history', icon: FileText, label: 'Histórico por Cliente' },
  ];

  const menuItems = user?.role === 'admin' ? adminMenuItems : companyMenuItems;

  const isActive = (path) => {
    if (path === '/admin' || path === '/company') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="w-64 bg-white shadow-sm border-r min-h-screen">
      <nav className="mt-8">
        <div className="px-3">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isRentalWithoutAccess = item.path === '/company/rentals' && !hasRentalAccess && user?.role === 'company';
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`
                      flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group
                      ${isActive(item.path)
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : isRentalWithoutAccess
                        ? 'text-gray-400 hover:text-gray-500 hover:bg-gray-50 cursor-not-allowed'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                      }
                    `}
                    onClick={isRentalWithoutAccess ? (e) => e.preventDefault() : undefined}
                    title={isRentalWithoutAccess ? 'Acesso restrito - solicite permissão ao administrador' : undefined}
                  >
                    <Icon className={`mr-3 h-5 w-5 transition-colors ${isRentalWithoutAccess ? 'text-gray-400' : ''}`} />
                    <span className={`transition-colors ${isRentalWithoutAccess ? 'line-through decoration-gray-400' : ''}`}>
                      {item.label}
                    </span>
                    {isRentalWithoutAccess && (
                      <span className="ml-auto relative">
                        <svg className="h-4 w-4 text-red-400 hover:text-red-500 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        <div className="hidden group-hover:block absolute right-0 top-6 w-48 bg-gray-900 text-white text-xs rounded py-2 px-3 z-50">
                          Módulo restrito. Entre em contato com o administrador.
                          <div className="absolute bottom-full right-2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                        </div>
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        
        <div className="mt-8 px-3">
          <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Configurações
          </h3>
          <ul className="mt-2 space-y-1">
            <li>
              <Link
                to={user?.role === 'admin' ? '/admin/settings' : '/company/settings'}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:text-gray-900 hover:bg-gray-50 transition-colors"
              >
                <Settings className="mr-3 h-5 w-5" />
                Configurações
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
